import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  GraduationCap,
  Building,
  Calendar,
  Zap,
  Clock,
  CalendarDays,
  ChevronRight,
  ChevronDown,
  LogOut,
  Shield,
  User,
  HelpCircle,
  Settings,
  IndianRupee,
  Database,
  FilePlus,
  FileText,
  BarChart3,
  Trash2,
  CheckSquare
} from 'lucide-react';

const Sidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isSuperAdmin, isTeacher } = useAuth();
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Role-based menu items with groups
  const adminMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard'
    },
    {
      id: 'admin-panel',
      label: 'Admin Panel',
      icon: Shield,
      type: 'group',
      items: [
        {
          id: 'employees',
          label: 'Members (Employees)',
          icon: Users,
          path: '/admin-panel'
        },
        {
          id: 'admin-permissions',
          label: 'Admin Permissions',
          icon: Shield,
          path: '/admin-permissions',
          superAdminOnly: true
        },
        {
          id: 'activity-logs',
          label: 'Activity Logs',
          icon: FileText,
          path: '/activity-logs',
          superAdminOnly: true
        },
        {
          id: 'backup-management',
          label: 'Backup Management',
          icon: Database,
          path: '/backup-management',
          superAdminOnly: true
        },
        {
          id: 'super-admin-settings',
          label: 'Super Admin Settings',
          icon: Settings,
          path: '/admin-panel',
          superAdminOnly: true
        }
      ]
    },
    {
      id: 'smart-masters',
      label: 'Smart Masters',
      icon: Database,
      type: 'group',
      items: [
        {
          id: 'batches',
          label: 'Batches',
          icon: Users,
          path: '/batches'
        },
        {
          id: 'subjects',
          label: 'Subjects',
          icon: BookOpen,
          path: '/subjects'
        },
        {
          id: 'classrooms',
          label: 'Classrooms',
          icon: Building,
          path: '/classrooms'
        },
        {
          id: 'sessions',
          label: 'Session Management',
          icon: Clock,
          path: '/sessions'
        },
        {
          id: 'holidays',
          label: 'Holiday Management',
          icon: Calendar,
          path: '/holidays'
        },
        {
          id: 'time-slots',
          label: 'Time Slots',
          icon: Clock,
          path: '/time-slots'
        },
        {
          id: 'data-cleanup',
          label: 'Data Cleanup',
          icon: Trash2,
          path: '/data-cleanup'
        }
      ]
    },
    {
      id: 'smart-builder',
      label: 'Smart Builder',
      icon: Zap,
      path: '/smart-builder'
    },
    {
      id: 'smart-timetables',
      label: 'Smart Time Tables',
      icon: CalendarDays,
      path: '/smart-timetables'
    },
    {
      id: 'exam-timetable',
      label: 'Exam Timetable',
      icon: FileText,
      path: '/exam-timetable'
    },
    {
      id: 'smart-payroll',
      label: 'Smart Payroll',
      icon: IndianRupee,
      type: 'group',
      items: [
        {
          id: 'payroll-overview',
          label: 'Overview',
          icon: LayoutDashboard,
          path: '/payroll-management?tab=overview'
        },
        {
          id: 'payroll-leaves',
          label: 'Leaves',
          icon: Calendar,
          path: '/payroll-management?tab=leaves'
        },
        {
          id: 'payroll-employees',
          label: 'Payroll',
          icon: Users,
          path: '/payroll-management?tab=payroll'
        },
        {
          id: 'payroll-salary-slips',
          label: 'Salary Slips',
          icon: FileText,
          path: '/payroll-management?tab=salary-slips'
        },
        {
          id: 'my-salary-slips',
          label: 'My Salary Slips',
          icon: FileText,
          path: '/my-salary-slips'
        },
        {
          id: 'payroll-documents',
          label: 'Generate Documents',
          icon: FilePlus,
          path: '/payroll-management?tab=documents'
        }
      ]
    },
    {
      id: 'expense-management',
      label: 'Expense Management',
      icon: IndianRupee,
      path: '/expense-management'
    },
    {
      id: 'tasks',
      label: 'Task Management',
      icon: CheckSquare,
      path: '/tasks'
    },
    {
      id: 'user-manual',
      label: 'User Manual',
      icon: BookOpen,
      path: '/user-manual'
    },
    {
      id: 'help',
      label: 'Help & Instructions',
      icon: HelpCircle,
      path: '/help'
    }
  ];

  const teacherMenuItems = [
    {
      id: 'teacher-dashboard',
      label: 'Teacher Dashboard',
      icon: LayoutDashboard,
      path: '/teacher-dashboard'
    },
    {
      id: 'smart-builder',
      label: 'Smart Builder',
      icon: Zap,
      path: '/smart-builder'
    },
    {
      id: 'smart-timetables',
      label: 'Smart Time Tables',
      icon: CalendarDays,
      path: '/smart-timetables'
    },
    {
      id: 'exam-timetable',
      label: 'Exam Timetable',
      icon: FileText,
      path: '/exam-timetable'
    },
    {
      id: 'my-leaves',
      label: 'My Leaves',
      icon: Calendar,
      path: '/my-leaves'
    },
    {
      id: 'my-salary-slips',
      label: 'My Salary Slips',
      icon: FileText,
      path: '/my-salary-slips'
    },
    {
      id: 'user-manual',
      label: 'User Manual',
      icon: BookOpen,
      path: '/user-manual'
    },
    {
      id: 'help',
      label: 'Help & Instructions',
      icon: HelpCircle,
      path: '/help'
    }
  ];

  const getMenuItems = () => {
    let items = [];
    if (isAdmin()) {
      items = adminMenuItems;
    } else if (isTeacher()) {
      items = teacherMenuItems;
    }
    
    // Filter out superAdminOnly items for non-super admins
    return items.map(item => {
      if (item.superAdminOnly && !isSuperAdmin()) {
        return null;
      }
      
      // If it's a group, filter its sub-items too
      if (item.type === 'group' && item.items) {
        const filteredItems = item.items.filter(subItem => {
          if (subItem.superAdminOnly && !isSuperAdmin()) {
            return false;
          }
          return true;
        });
        
        return {
          ...item,
          items: filteredItems
        };
      }
      
      return item;
    }).filter(Boolean); // Remove null items
  };

  const menuItems = getMenuItems();

  const handleNavigation = (item) => {
    setActiveTab(item.id);
    navigate(item.path);
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  return (
    <div className="w-64 bg-white shadow-xl border-r border-gray-100 h-full flex flex-col">
      {/* Header with Logo */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-col items-center mb-4">
          <img 
            src="/nif_logo_small_clean.png" 
            alt="NIF Global" 
            className="h-10 w-auto object-contain mb-2"
          />
          <h1 className="text-lg font-bold text-gray-900 text-center mb-1">Smart Manager</h1>
          <p className="text-xs text-gray-500 text-center">NIF Global Pimpri Chinchwad</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          if (item.type === 'group') {
            const isCollapsed = collapsedGroups[item.id];
            const Icon = item.icon;
            const hasActiveItem = item.items.some(subItem => location.pathname === subItem.path);
            
            return (
              <div key={item.id} className="space-y-1">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(item.id)}
                  className={`w-full flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 group ${
                    hasActiveItem
                      ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon className={`w-5 h-5 mr-3 transition-colors ${
                    hasActiveItem ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`} />
                  <span className="font-medium flex-1">{item.label}</span>
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                {/* Group Items */}
                {!isCollapsed && (
                  <div className="ml-6 space-y-1">
                    {item.items.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isActive = location.pathname === subItem.path;
                      
                      return (
                        <button
                          key={subItem.id}
                          onClick={() => handleNavigation(subItem)}
                          className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-all duration-200 group ${
                            isActive
                              ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                          data-testid={`sidebar-${subItem.id}`}
                        >
                          <SubIcon className={`w-4 h-4 mr-3 transition-colors ${
                            isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                          }`} />
                          <span className="font-medium flex-1 text-sm">{subItem.label}</span>
                          {isActive && (
                            <ChevronRight className="w-3 h-3 text-blue-600" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          } else {
            // Regular menu item
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 group ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className={`w-5 h-5 mr-3 transition-colors ${
                  isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                }`} />
                <span className="font-medium flex-1">{item.label}</span>
                {isActive && (
                  <ChevronRight className="w-4 h-4 text-blue-600" />
                )}
              </button>
            );
          }
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-100 space-y-3">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
            isAdmin() ? 'bg-blue-600' : 'bg-green-600'
          }`}>
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/change-password')}
          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4" />
          <span>Change Password</span>
        </button>
        
        <button
          onClick={logout}
          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;